import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UI_SIDEBAR_COLLAPSED_KEY } from 'src/app/core/models/ui-theme';
import { ApplicationUserService } from 'src/app/core/services/application-user.service';
import { MenuItem } from 'src/app/models/menu/menu.model';

/**
 * Modern kabuk sol menüsü.
 *
 * Klasik sidebar'dan farkı: MetisMenu ve belge geneli DOM manipülasyonu yok.
 * Açık/kapalı durum bileşen state'inde tutulur, aktif dal rota değişiminde
 * kendiliğinden açılır (bkz. yeni-tasarim-plan.md, Risk R3).
 *
 * Menü kaynağı klasik sidebar ile aynıdır: ApplicationUserService -> menuItemList.
 */
@Component({
  selector: 'app-modern-sidebar',
  templateUrl: './modern-sidebar.component.html',
  styleUrls: ['./modern-sidebar.component.scss']
})
export class ModernSidebarComponent implements OnInit, OnDestroy {

  /** Mobilde çekmece açık mı (992px altı). */
  @Input() mobileOpen = false;

  /** Menü daraltıldığında/genişletildiğinde kabuğun düzeni güncellemesi için. */
  @Output() collapsedChange = new EventEmitter<boolean>();

  /** Mobilde bir bağlantıya tıklanınca çekmece kapansın diye. */
  @Output() closeRequested = new EventEmitter<void>();

  menuItems: MenuItem[] = [];
  collapsed = false;

  /** Hover ile açılan uçan alt menünün ait olduğu öğe (bkz. opensAsFlyout). */
  flyoutItem: MenuItem = null;
  flyoutTop = 0;
  flyoutLeft = 0;

  private flyoutCloseTimer: any = null;

  /**
   * Uçan panel yalnızca gerçek hover'ı olan geniş ekranlarda kullanılabilir.
   *
   * Dokunmatikte hover yok, 992px altında sol menü çekmeceye dönüşüyor; bu
   * ekranlarda panelle açılan dallar tamamen erişilemez kalırdı. Oralarda satır
   * içi akordiyona düşülür.
   */
  private canUseFlyout = true;
  private flyoutMedia: MediaQueryList = null;
  private readonly onFlyoutMediaChange = (event: MediaQueryListEvent | MediaQueryList) => {
    this.canUseFlyout = event.matches;

    if (!this.canUseFlyout) {
      this.cancelFlyoutClose();
      this.flyoutItem = null;
    }
  };
  subSystemName = localStorage.getItem('subSystemName') || 'sarraf.io';

  /**
   * DB kayitlarinda farkli menu ogeleri ayni/eksik id ile gelebiliyor.
   * Bu nedenle accordion durumunu turetilmis bir anahtar yerine menu nesnesiyle tutariz.
   */
  private readonly expandedItems = new Set<MenuItem>();
  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private applicationUserService: ApplicationUserService
  ) { }

  ngOnInit(): void {
    this.collapsed = this.readCollapsed();

    this.flyoutMedia = window.matchMedia('(min-width: 992px) and (hover: hover)');
    this.onFlyoutMediaChange(this.flyoutMedia);
    this.flyoutMedia.addEventListener('change', this.onFlyoutMediaChange);

    this.subscriptions.add(
      this.applicationUserService.currentUser.subscribe(user => {
        this.menuItems = user?.menuItemList || [];
        this.syncExpandedWithUrl();
      })
    );

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.syncExpandedWithUrl();
          this.cancelFlyoutClose();
          this.flyoutItem = null;
          this.closeRequested.emit();
        })
    );
  }

  ngOnDestroy(): void {
    this.cancelFlyoutClose();
    this.flyoutMedia?.removeEventListener('change', this.onFlyoutMediaChange);
    this.subscriptions.unsubscribe();
  }

  get brandMark(): string {
    return (this.subSystemName || 's').trim().charAt(0).toUpperCase();
  }

  hasItems(item: MenuItem): boolean {
    return (item.childrenMenuItem?.length || 0) > 0;
  }

  isExpanded(item: MenuItem): boolean {
    return this.expandedItems.has(item);
  }

  /**
   * Grubun alt menüsü satır içi akordiyon yerine yanda açılan panelde mi gösterilir.
   *
   * Daraltılmış menüde birinci seviye (satır içi yer yok), genişletilmiş menüde
   * ikinci seviye ve sonrası (akordiyon derinleştikçe okunaksızlaşıyordu).
   * Panelin kendi içindeki gruplar zaten açık listelendiği için hariç tutulur.
   */
  opensAsFlyout(item: MenuItem, depth: number, inFlyout: boolean): boolean {
    if (inFlyout || !this.canUseFlyout || !this.hasItems(item)) {
      return false;
    }

    return this.collapsed ? depth === 0 : depth > 0;
  }

  toggleItem(item: MenuItem, depth: number, inFlyout: boolean): void {
    // Uçan panelde açılan gruplarda tıklanacak bir hedef yok; panel hover ile
    // yönetiliyor.
    if (inFlyout || this.opensAsFlyout(item, depth, inFlyout)) {
      return;
    }

    if (this.expandedItems.has(item)) {
      this.expandedItems.delete(item);
      return;
    }

    this.expandedItems.add(item);
  }

  /**
   * Uçan panelle açılan bir gruba gelince paneli konumlandırır ve açar.
   *
   * Panel `position: fixed` ile konumlanıyor: sol menünün kaydırma alanı
   * `overflow: auto` olduğu için normal akışta konumlanan bir panel kırpılırdı.
   * Sola değil menünün sağ kenarına hizalanır; ikinci seviye satırlar girintili
   * olduğu için satırın kendi sağ kenarı kullanılsaydı panel menünün üstüne binerdi.
   */
  onItemHover(item: MenuItem, event: MouseEvent, depth: number, inFlyout: boolean): void {
    if (!this.opensAsFlyout(item, depth, inFlyout)) {
      return;
    }

    const element = event.currentTarget as HTMLElement;
    const rect = element.getBoundingClientRect();
    const sidebar = element.closest('.m-sidebar');
    // Panel içinde alt gruplar da açık listelendiği için torun satırlar da sayılır;
    // yalnızca doğrudan çocuklar sayılsaydı üç seviyeli dallarda panel aşağı taşardı.
    const estimatedHeight = this.countVisibleRows(item.childrenMenuItem) * 34 + 52;

    this.cancelFlyoutClose();
    this.flyoutLeft = sidebar ? sidebar.getBoundingClientRect().right : rect.right;
    this.flyoutTop = Math.max(8, Math.min(rect.top, window.innerHeight - estimatedHeight - 12));
    this.flyoutItem = item;
  }

  onItemLeave(item: MenuItem, depth: number, inFlyout: boolean): void {
    if (this.flyoutItem === item && this.opensAsFlyout(item, depth, inFlyout)) {
      this.scheduleFlyoutClose();
    }
  }

  /**
   * Panel satırın sağ kenarından biraz uzakta açıldığı için imleç aradaki boşluktan
   * geçerken `mouseleave` tetikleniyor; kısa gecikme paneli kapanmadan yakalamayı
   * mümkün kılıyor.
   */
  scheduleFlyoutClose(): void {
    this.cancelFlyoutClose();
    this.flyoutCloseTimer = setTimeout(() => {
      this.flyoutItem = null;
      this.flyoutCloseTimer = null;
    }, 180);
  }

  cancelFlyoutClose(): void {
    if (this.flyoutCloseTimer !== null) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  /** Uçan panelde görünecek toplam satır sayısı (alt gruplar panelde hep açıktır). */
  private countVisibleRows(items?: MenuItem[]): number {
    return (items || [])
      .filter(item => item.isActive !== false)
      .reduce((total, item) => total + 1 + this.countVisibleRows(item.childrenMenuItem), 0);
  }

  isFlyoutOpen(item: MenuItem): boolean {
    return this.flyoutItem === item;
  }

  /** Öğenin kendisi ya da alt dallarından biri geçerli rota mı. */
  isBranchActive(item: MenuItem): boolean {
    if (this.isLinkActive(item.link)) {
      return true;
    }

    return (item.childrenMenuItem || []).some(child => this.isBranchActive(child));
  }

  isLinkActive(link?: string): boolean {
    const normalized = this.normalize(link);

    if (!normalized) {
      return false;
    }

    const url = this.router.url.split('?')[0];
    return url === normalized || url.startsWith(normalized + '/');
  }

  toggleCollapse(): void {
    this.setCollapsed(!this.collapsed);
  }

  private setCollapsed(value: boolean): void {
    this.collapsed = value;
    this.cancelFlyoutClose();
    this.flyoutItem = null;
    this.collapsedChange.emit(value);

    try {
      localStorage.setItem(UI_SIDEBAR_COLLAPSED_KEY, String(value));
    } catch (e) {
      // depolama kapalıysa tercih yalnızca bu oturumda geçerli olur
    }
  }

  private readCollapsed(): boolean {
    try {
      return localStorage.getItem(UI_SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  /** Geçerli rotayı içeren tüm dalları açar; kullanıcının elle açtıklarını kapatmaz. */
  private syncExpandedWithUrl(): void {
    this.menuItems.forEach(item => this.expandActiveBranch(item));
  }

  private expandActiveBranch(item: MenuItem): boolean {
    const children = item.childrenMenuItem || [];
    let active = this.isLinkActive(item.link);

    children.forEach(child => {
      if (this.expandActiveBranch(child)) {
        active = true;
      }
    });

    if (active && children.length > 0) {
      this.expandedItems.add(item);
    }

    return active;
  }

  /** DB'deki linkler 'case/balances/' gibi gelebiliyor; '/case/balances' biçimine indirger. */
  private normalize(link?: string): string {
    if (!link) {
      return '';
    }

    const trimmed = link.trim().replace(/^\/+/, '').replace(/\/+$/, '');
    return trimmed ? '/' + trimmed : '';
  }
}
